import { useState, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';
import { APP_CONFIG } from '@/constants/constants';
import { ProductActionWrite, SortDirection, FormStatus, ProductActionRead } from '@/types';
import { logger } from '@/utils/secureLogger';
import { trackStockAdjustment } from '@/services/analytics';
import { getProductActionDocs, getProductactionsByPaths, incrementProduct, decrementProduct, adjustProductInventory, deleteProductActionDoc } from '@/services/firebaseApi';

const invalidateAfterProductAction = (queryClient: QueryClient, variables: ProductActionWrite) => {
    const sid = variables.ids?.storageId;
    const pid = variables.ids?.productId;
    // Linked transactions are identified by Firestore document id (`transaction.id`),
    // not the numeric display id (`transaction.transactionId`).
    const transactionDocId = variables.transaction?.id;
    if (!sid || !pid) return;
    queryClient.invalidateQueries({ queryKey: ['actions', sid, pid], exact: false });
    queryClient.invalidateQueries({ queryKey: ['actionsByPaths', sid], exact: false });
    queryClient.invalidateQueries({ queryKey: ['products', sid] });
    queryClient.invalidateQueries({ queryKey: ['product', sid, pid] });
    queryClient.invalidateQueries({ queryKey: ['productHistory', sid, pid], exact: false });
    queryClient.invalidateQueries({ queryKey: ['transactions', sid] });
    if (transactionDocId) {
        queryClient.invalidateQueries({ queryKey: ['transaction', sid, transactionDocId] });
    } else {
        queryClient.invalidateQueries({ queryKey: ['transaction', sid], exact: false });
    }
    queryClient.invalidateQueries({ queryKey: ['storage', sid] });
    queryClient.invalidateQueries({ queryKey: ['storages'] });
};

export enum ActionsSortBy {
    CREATED_AT = 'keyHistory.createdAt',
    AMOUNT = 'amount',
}

interface UseActionsProps {
    storageId?: string;
    productId?: string;
    actionPaths?: string[];
    defaultDocLimit?: number;
    transactionId?: string;
}

const useActions = ({ 
    storageId, 
    productId,
    actionPaths,
    defaultDocLimit = APP_CONFIG.DEFAULT_DOC_LIMIT,
    transactionId,
}: UseActionsProps) => {
    const queryClient = useQueryClient();
    
    // Filter states
    const [sortBy, setSortBy] = useState(ActionsSortBy.CREATED_AT);
    const [sortDirection, setSortDirection] = useState(SortDirection.DESCENDING);
    const [filterQuery, setFilterQuery] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('all');

    // Infinite query for paginated actions (by product)
    const {
        data: actionsData,
        isLoading: actionsLoading,
        error: actionsError,
        fetchNextPage,
        hasNextPage,
        refetch: refetchActions
    } = useInfiniteQuery({
        queryKey: ['actions', storageId, productId, sortBy, sortDirection],
        queryFn: ({ pageParam }: { pageParam: string | undefined }) => getProductActionDocs({
            storageId: storageId!,
            productId: productId!,
            docLimit: defaultDocLimit,
            startAfterId: pageParam,
            sortBy,
            sortDirection
        }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage: ProductActionRead[]) => {
            if (lastPage.length < defaultDocLimit) {
                return undefined; // No more pages
            }
            const lastItem = lastPage[lastPage.length - 1];
            // Use the document ID as the cursor for pagination
            return lastItem.id;
        },
        enabled: !!(storageId && productId && !actionPaths),
        staleTime: 2 * 60 * 1000, // Actions change frequently
        gcTime: 5 * 60 * 1000,
    });

    // Query for actions by paths (for transaction page)
    const {
        data: actionsByPaths,
        isLoading: actionsByPathsLoading,
        error: actionsByPathsError,
        refetch: refetchActionsByPaths
    } = useQuery({
        queryKey: ['actionsByPaths', storageId, transactionId, actionPaths],
        queryFn: () => getProductactionsByPaths({
            storageId: storageId!,
            actionPaths: actionPaths!,
            transactionId: transactionId!,
        }),
        enabled: !!(storageId && transactionId && actionPaths && actionPaths.length > 0),
        staleTime: 0,
    });

    // Flatten the infinite query data or use actionsByPaths
    const allActions = useMemo(() => {
        if (actionPaths) {
            return (actionsByPaths as ProductActionRead[]) || [];
        }
        return (actionsData?.pages.flat() as ProductActionRead[]) || [];
    }, [actionsData, actionsByPaths, actionPaths]);

    // Apply client-side filtering
    const filteredActions = useMemo(() => {
        if (!allActions) return [];
        
        let filtered = [...allActions];

        // Filter by type
        if (typeFilter && typeFilter !== 'all') {
            filtered = filtered.filter((action) => action.type === typeFilter);
        }

        // Filter by search query
        if (filterQuery) {
            const lower = filterQuery.toLowerCase();
            filtered = filtered.filter((action) =>
                (action.description && action.description.toLowerCase().includes(lower)) ||
                (action.createdBy?.name && action.createdBy.name.toLowerCase().includes(lower)) ||
                (action.transaction?.transactionId && action.transaction.transactionId.toString().includes(lower))
            );
        }
        return filtered;
    }, [allActions, filterQuery, typeFilter]);

    // Mutations for product actions
    const incrementProductMutation = useMutation({
        mutationFn: incrementProduct,
        onSuccess: (_data, variables) => {
            trackStockAdjustment('increment');
            logger.info('Product incremented successfully');
            invalidateAfterProductAction(queryClient, variables);
        },
        onError: (error) => {
            logger.error('Failed to increment product:', error, 'useActions');
        }
    });

    const decrementProductMutation = useMutation({
        mutationFn: decrementProduct,
        onSuccess: (_data, variables) => {
            trackStockAdjustment('decrement');
            logger.info('Product decremented successfully');
            invalidateAfterProductAction(queryClient, variables);
        },
        onError: (error) => {
            logger.error('Failed to decrement product:', error, 'useActions');
        }
    });

    const adjustInventoryMutation = useMutation({
        mutationFn: adjustProductInventory,
        onSuccess: (_data, variables) => {
            trackStockAdjustment('adjust');
            logger.info('Product inventory adjusted successfully');
            invalidateAfterProductAction(queryClient, variables);
        },
        onError: (error) => {
            logger.error('Failed to adjust product inventory:', error, 'useActions');
        }
    });

    const deleteProductActionMutation = useMutation({
        mutationFn: ({ storageId: sid, productId: pid, actionId }: { storageId: string; productId: string; actionId: string; transactionDocId?: string }) =>
            deleteProductActionDoc({ storageId: sid, productId: pid, actionId }),
        onSuccess: (_data, variables) => {
            logger.info('Draft product action deleted');
            queryClient.invalidateQueries({ queryKey: ['actions', variables.storageId, variables.productId], exact: false });
            queryClient.invalidateQueries({ queryKey: ['actionsByPaths', variables.storageId], exact: false });
            queryClient.invalidateQueries({ queryKey: ['products', variables.storageId] });
            queryClient.invalidateQueries({ queryKey: ['product', variables.storageId, variables.productId] });
            queryClient.invalidateQueries({ queryKey: ['transactions', variables.storageId] });
            if (variables.transactionDocId) {
                queryClient.invalidateQueries({ queryKey: ['transaction', variables.storageId, variables.transactionDocId] });
            } else {
                queryClient.invalidateQueries({ queryKey: ['transaction', variables.storageId], exact: false });
            }
        },
        onError: (error) => {
            logger.error('Failed to delete product action:', error, 'useActions');
        }
    });

    // Helper function to determine status
    const getStatus = (loading: boolean, error: any): FormStatus => {
        if (loading) return FormStatus.LOADING;
        if (error) return FormStatus.ERROR;
        return FormStatus.SUCCESS;
    };

    // Determine which loading and error state to use
    const isLoading = actionPaths ? actionsByPathsLoading : actionsLoading;
    const error = actionPaths ? actionsByPathsError : actionsError;

    // Backward compatible functions
    const reloadActions = () => {
        if (actionPaths) {
            refetchActionsByPaths();
        } else {
            refetchActions();
        }
    };

    return {
        // Multiple actions
        actions: allActions,
        filteredActions,
        actionsStatus: getStatus(isLoading, error),
        
        // Pagination
        hasNextPage,
        fetchNextPage,
        
        // Filtering and sorting
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        filterQuery,
        setFilterQuery,
        typeFilter,
        setTypeFilter,
        
        // Actions
        reloadActions,
        
        // Mutations (async only)
        incrementProductAsync: incrementProductMutation.mutateAsync,
        decrementProductAsync: decrementProductMutation.mutateAsync,
        adjustInventoryAsync: adjustInventoryMutation.mutateAsync,
        deleteProductActionAsync: deleteProductActionMutation.mutateAsync,
        isIncrementing: incrementProductMutation.isPending,
        isDecrementing: decrementProductMutation.isPending,
        isAdjusting: adjustInventoryMutation.isPending,
        isDeletingAction: deleteProductActionMutation.isPending,
        
        // Refresh functions
        refreshActions: refetchActions,
    };
};

export default useActions;
